import React from 'react'
import { MultiFileUpload } from '../../components/forms'
import { getDocSubTypeRequiredCount, SolarDoc } from '@oneethos/shared'
import type { DocSubType, PaystubSubType, TaxReturnSubType } from '@oneethos/shared'

const DOC_TYPES: { value: string; label: string; message: string | React.ReactNode | null }[] = [
  { value: 'W-2', label: 'W-2', message: 'One W-2 required' },
  { value: 'paystubs', label: 'Paystubs', message: null },
  { value: 'taxreturn', label: 'Tax Return', message: null },
  { value: 'k1', label: 'K1', message: 'One K-1 required' },
  { value: 'tax1099', label: '1099', message: 'One 1099 required' },
  { value: 'retirementBenefitLetter', label: 'Retirement Benefit Letter', message: 'One Retirement Benefit Letter required' },
  { value: 'vaDisabilityLetter', label: 'VA Disability Letter', message: 'One VA Disability Letter required' },
  { value: 'socialSecurityAwardLetter', label: 'Social Security Award Letter', message: 'One Social Security Award Letter required' },
]

const PAY_FREQUENCY_OPTIONS: { value: PaystubSubType; label: string, message: string }[] = [
  { value: 'weekly', label: 'Weekly', message: '4 paystubs required' },
  { value: 'biWeekly', label: 'Bi-Weekly / Semi-Monthly', message: '2 paystubs required' },
  { value: 'monthly', label: 'Monthly or Longer', message: '1 paystub required' },
]

const TAX_RETURN_OPTIONS: { value: TaxReturnSubType; label: string, message: string | React.ReactNode | null }[] = [
  { value: 'joint', label: 'Joint', message: <div>One <strong>Jointly filed Tax Return with co-borrower</strong> required</div> },
  { value: 'individual', label: 'Individual', message: <div>One <strong>Individual (not jointly filed) Tax Return</strong> required</div> },
]

const getNumRequired = (
  docType: string,
  docSubType: DocSubType | undefined,
  isJointApp: boolean
): number | null => {
  if (docType === 'paystubs') {
    return getDocSubTypeRequiredCount(docSubType)
  }
  if (docType === 'taxreturn') {
    if (!isJointApp) return 1
    return getDocSubTypeRequiredCount(docSubType)
  }
  return 1
}

interface DocSelectorProps {
  docType: string
  docSubType?: DocSubType
  onDocTypeChange: (ev: React.ChangeEvent<HTMLSelectElement>) => void
  onDocSubTypeChange?: (ev: React.ChangeEvent<HTMLSelectElement>) => void
  onChange: (docs: File[]) => void
  docs: (File & SolarDoc)[] | File[]
  isJointApp: boolean
}

const DocSelector = ({
  docType,
  docSubType,
  onDocTypeChange,
  onDocSubTypeChange,
  onChange,
  docs,
  isJointApp,
}: DocSelectorProps) => {
  const numRequired = getNumRequired(docType, docSubType, isJointApp)

  const showFileUpload = (docType, docSubType) => {
    if (!docType) return false
    if (docType === 'paystubs') return !!docSubType
    if (docType === 'taxreturn' && isJointApp) return !!docSubType
    if (docType === 'taxreturn' && !isJointApp) return true
    return true 
  }

  return (
    <div className="form-group mb-5">
      <label>Select Documentation Type</label>
      <select value={docType} className="form-select" onChange={onDocTypeChange}>
        <option value="">(make selection)</option>
        {DOC_TYPES.map((doc) => (
          <option key={doc.value} value={doc.value}>
            {doc.label}
          </option>
        ))}
      </select>

      {DOC_TYPES.map((doc) => (
        <div key={doc.value} className="my-2">
          {doc.value === docType && doc.message}
        </div>
      ))}

      {docType === 'paystubs' && (
        <>
          <label className="mt-2">Select Pay Frequency</label>
          <select
            className="form-select"
            value={docSubType}
            onChange={onDocSubTypeChange}
          >
            <option value="">(make selection)</option>
            {PAY_FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {docSubType && (
            <div className="my-2" aria-live="polite">
              {PAY_FREQUENCY_OPTIONS.find((opt) => opt.value === docSubType)?.message}
            </div>
          )}
        </>
      )}

      {docType === 'taxreturn' && !isJointApp && (
        <div className="my-2">
          {TAX_RETURN_OPTIONS.find((opt) => opt.value === 'individual')?.message}
        </div>
      )}

      {docType === 'taxreturn' && isJointApp && (
        <>
          <label className="mt-2">Select Tax Return Type</label>
          <select className="form-select" value={docSubType} onChange={onDocSubTypeChange}>
            <option value="">(make selection)</option>
            {TAX_RETURN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {docSubType && (
            <div className="my-2" aria-live="polite">
              {TAX_RETURN_OPTIONS.find((opt) => opt.value === docSubType)?.message}
            </div>
          )}
        </>
      )}

      {showFileUpload(docType, docSubType) && (
        <MultiFileUpload
          docs={(docs ?? []) as (File & SolarDoc)[]}
          onChange={onChange}
          numRequired={numRequired}
        />
      )}
    </div>
  )
}

export default DocSelector